import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiAlertTriangle, FiClock, FiCheckCircle, FiArrowRight, FiBookOpen, FiX, FiPlay, FiEye, FiFileText } = FiIcons;

const InvestigationTrigger = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { reports } = useData();
  const { investigations, getActiveNotifications, markNotificationAsRead, startInvestigation } = useInvestigation();
  
  const [showTrigger, setShowTrigger] = useState(false);
  const [activeInvestigation, setActiveInvestigation] = useState(null);
  const [activeNotificationId, setActiveNotificationId] = useState(null);

  useEffect(() => {
    if (!user || !user.companyId) return;
    
    // Check for "investigation_required" notifications that are unread
    const activeNotifs = getActiveNotifications();
    const relevantNotification = activeNotifs.find(n => {
      const isTypeMatch = n.type === 'investigation_required' || n.requiresAction;
      const isUnread = n.status !== 'read';
      // Verify report belongs to user's company
      const associatedReport = reports.find(r => r.id === n.reportId);
      const belongsToCompany = associatedReport && associatedReport.companyId === user.companyId;
      return isTypeMatch && isUnread && belongsToCompany;
    });

    if (relevantNotification) {
      const investigation = investigations.find(inv => inv.reportId === relevantNotification.reportId);
      if (investigation) {
        setActiveInvestigation(investigation);
        setActiveNotificationId(relevantNotification.id);
        setShowTrigger(true);
      }
    } else {
      setShowTrigger(false);
    }
  }, [investigations, getActiveNotifications, user, reports]);

  const handleStart = () => {
    if (activeInvestigation && activeNotificationId) {
      startInvestigation(activeInvestigation.id);
      markNotificationAsRead(activeNotificationId);
      navigate('/company/investigation-guide', { state: { reportId: activeInvestigation.reportId, investigationId: activeInvestigation.id }});
      setShowTrigger(false);
    }
  };

  const handleDismiss = () => {
    if (activeNotificationId) {
      markNotificationAsRead(activeNotificationId);
      setShowTrigger(false);
    }
  };

  if (!showTrigger || !activeInvestigation) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border-l-4 border-red-500 overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 text-2xl"><SafeIcon icon={FiAlertTriangle} /></div>
              <div><h2 className="text-xl font-bold text-gray-900">Utredning kreves</h2><p className="text-gray-600">Nytt varsel krever handling</p></div>
            </div>
            <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} /></button>
          </div>
          <div className="p-6 bg-gray-50">
            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div><span className="text-gray-500">Sak:</span> <span className="font-medium">{activeInvestigation.reportTitle || activeInvestigation.investigationTitle}</span></div>
              <div><span className="text-gray-500">Alvorlighet:</span> <span className="font-bold text-red-600 uppercase">{activeInvestigation.severity}</span></div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900 mb-2">Neste steg:</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>1. Start utredningsprosess i systemet</li>
                <li>2. Gjennomfør innledende vurdering innen fristen ({activeInvestigation.dueDate})</li>
              </ul>
            </div>
          </div>
          <div className="p-6 flex justify-end gap-3">
            <button onClick={handleDismiss} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Utsett</button>
            <button onClick={handleStart} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><SafeIcon icon={FiBookOpen} /> Start utredning</button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InvestigationTrigger;